import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import {
  Button,
  ModalCloseBtn,
  theme,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTrackEffect } from '@order/Telemetry';

import {
  DietaryRestrictions,
  useDietaryRestrictionsMachine,
} from '../../components';
import { TransparentModal } from '../../components/TransparentModal';
import type { DietaryRestrictionsScreenProps } from '../../navigation/AppNavigation.props';

export const DietaryRestrictionsScreen = ({
  navigation,
}: DietaryRestrictionsScreenProps) => {
  const { t } = useLocalizationContext();
  const { minWidth, currentBreakpoint } = useResponsive();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { isModified, confirmRestrictions, revertRestrictions } =
    useDietaryRestrictionsMachine();

  const handleClose = React.useCallback(() => {
    navigation.getParent()?.goBack();
  }, [navigation]);

  const confirmDietaryRestrictions = React.useCallback(() => {
    confirmRestrictions();
    addNoticeBanner({
      text: t('dietary-restrictions.successful-change'),
      palette: 'success',
    });
  }, [addNoticeBanner, confirmRestrictions, t]);

  // revert unconfirmed dietary restrictions on screen blur
  useFocusEffect(
    React.useCallback(() => revertRestrictions, [revertRestrictions]),
  );

  useTrackEffect('dietary-restrictions.view', {});

  // ─────────────────────────────────────────────────────────────────
  return (
    <TransparentModal
      testID="dietary-restrictions"
      size="medium"
      onClose={handleClose}
    >
      {currentBreakpoint.isMobile ? null : (
        <ModalCloseBtn onPress={handleClose} />
      )}

      <ScrollView
        style={minWidth.tablet ? styles.containerSM : undefined}
        contentContainerStyle={styles.grow}
      >
        <View style={styles.wrapper}>
          <DietaryRestrictions
            shouldHideIllustration={currentBreakpoint.isMobile}
          />
        </View>

        <View style={styles.ctaWrapper}>
          <Button
            testID="dietary.restrictions.confirm"
            onPress={confirmDietaryRestrictions}
            size="large"
            style={styles.cta}
            focusOutlineColor={theme.colors.GRAY}
            disabled={!isModified}
          >
            {t('general.confirm')}
          </Button>
        </View>
      </ScrollView>
    </TransparentModal>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
    alignItems: 'center',
  },
  containerSM: {
    padding: theme.spacing.medium,
  },
  wrapper: {
    padding: theme.spacing.medium,
  },
  ctaWrapper: {
    paddingTop: theme.spacing.large,
    paddingBottom: theme.spacing.medium,
  },
  cta: {
    minWidth: 207,
  },
});
